import { getTeamMembers, getTeams, updateTeam } from 'api/teams'
import { useAuthContext } from 'components/context/AuthProvider'
import NotificationSys from 'components/NotificationSystem'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { CarePerson, Nullish, Team, TeamMember, TeamType, UserType } from 'types'

export const TeamsContext = createContext<{
  teamsData: Team[]
  activeTeamId: Nullish<number>
  activeTeamName: Nullish<string>
  activeTeamType: Nullish<TeamType>
  /** `care_persons` row for the active care team (`care_teams_v2.care_person_id`). */
  activeTeamCarePerson: CarePerson | null
  /** Open POC profile for the active team when present, else the logged-in user's `carePersonId`. */
  concernProfileCarePersonId: Nullish<number>
  teamMembersData: TeamMember[]
  isTeamMembersLoading: boolean
  checkIsDefault: (teamId: Nullish<number>) => boolean
  checkIsSystemManaged: (teamId: Nullish<number>) => boolean
  onChangeTeam: (teamId: Nullish<number>) => Promise<void>
  onChangeTeamToDefault: () => void
  onRefetchTeams: () => Promise<void>
  onRefetchTeamMembers: () => Promise<void>
} | null>(null)

export function TeamsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext()
  const queryClient = useQueryClient()
  const teamsQuery = useQuery('getTeams', getTeams)

  const teamsData = useMemo(() => {
    if (!teamsQuery.data) return []

    return teamsQuery.data.data.rows
  }, [teamsQuery])

  const [activeTeamIdLocal, setActiveTeamIdLocal] = useState<Nullish<number>>(null)
  const syncedActiveTeamRef = useRef<number | null>(null)

  const activeTeamId = useMemo(() => {
    const rows = teamsQuery.data?.data?.rows
    if (!rows?.length) return null

    if (activeTeamIdLocal) return activeTeamIdLocal

    const byActive = rows.find((team) => team.isActive)
    if (byActive?.id != null) return byActive.id

    if (user?.type?.includes(UserType.TRAINEE)) {
      const byLearning = rows.find((team) => team.isSystemManaged)
      if (byLearning?.id != null) return byLearning.id
    }

    const byDefault = rows.find((team) => team.isDefault)
    if (byDefault?.id != null) return byDefault.id

    return rows[0]?.id ?? null
  }, [activeTeamIdLocal, teamsQuery.data, user?.type])

  const activeTeamName = useMemo(() => {
    if (!activeTeamId || !teamsData.length) return null
    const team = teamsData.find((t) => t.id === activeTeamId)

    return team?.name ?? null
  }, [activeTeamId, teamsData])

  const activeTeamType = useMemo((): Nullish<TeamType> => {
    if (!activeTeamId || !teamsData.length) return null
    const team = teamsData.find((t) => t.id === activeTeamId)

    return team?.type ?? null
  }, [activeTeamId, teamsData])

  const activeTeamCarePerson = useMemo((): CarePerson | null => {
    if (!activeTeamId || !teamsData.length) return null
    const team = teamsData.find((t) => t.id === activeTeamId)

    return team?.carePerson ?? null
  }, [teamsData, activeTeamId])

  const concernProfileCarePersonId = useMemo((): Nullish<number> => {
    if (activeTeamCarePerson?.id != null) {
      return activeTeamCarePerson.id
    }
    if (!teamsQuery.isFetched) {
      return null
    }
    if (teamsData.length > 0) {
      return null
    }
    return user?.carePersonId ?? null
  }, [activeTeamCarePerson, teamsQuery.isFetched, teamsData.length, user?.carePersonId])

  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ['personOfConcern'] })
  }, [concernProfileCarePersonId, queryClient])

  const updateTeamMutation = useMutation(updateTeam)

  const saveActiveTeam = useCallback(
    async (teamId: number) => {
      try {
        await updateTeamMutation.mutateAsync({
          id: teamId,
          isActive: true,
        })
      } catch (e) {
        NotificationSys.showWarning('An error occurred while update the team')
        console.error(e)
      }
    },
    [updateTeamMutation],
  )

  // UI may fall back to a team that is not yet active for this user (e.g. invited caregiver).
  // Persist that selection so share and other APIs accept the same team.
  useEffect(() => {
    if (!activeTeamId || !teamsQuery.isSuccess) return

    const selected = teamsData.find((team) => team.id === activeTeamId)
    if (!selected || selected.isActive) return
    if (syncedActiveTeamRef.current === activeTeamId) return

    syncedActiveTeamRef.current = activeTeamId
    void saveActiveTeam(activeTeamId).then(() => {
      void teamsQuery.refetch()
    })
  }, [activeTeamId, teamsData, teamsQuery, saveActiveTeam])

  const handleChangeTeam = useCallback(
    async (teamId: Nullish<number>) => {
      setActiveTeamIdLocal(teamId)
      if (teamId) {
        // Prevent the inactive-team sync effect from firing a second activate call.
        syncedActiveTeamRef.current = teamId
        await saveActiveTeam(teamId)
      }
    },
    [saveActiveTeam],
  )

  const handleChangeTeamToDefault = useCallback(async () => {
    const defaultTeam = teamsData.find((team) => team.isDefault)
    const teamId = defaultTeam ? defaultTeam.id : null
    await handleChangeTeam(teamId)
  }, [handleChangeTeam, teamsData])

  const checkIsDefault = useCallback(
    (teamId: Nullish<number>) => {
      const team = teamsData.find((team) => team.id === teamId)
      return team ? team.isDefault : false
    },
    [teamsData],
  )

  const checkIsSystemManaged = useCallback(
    (teamId: Nullish<number>) => {
      const team = teamsData.find((team) => team.id === teamId)
      return team ? team.isSystemManaged : false
    },
    [teamsData],
  )

  const handleRefetchTeams = useCallback(async () => {
    await teamsQuery.refetch()
  }, [teamsQuery])

  const teamMemberQuery = useQuery(
    ['getTeamMember', activeTeamId],
    () => {
      if (!activeTeamId) return null

      return getTeamMembers({
        id: activeTeamId,
      })
    },
    {
      enabled: activeTeamId !== null,
    },
  )

  const teamMembersData = useMemo(() => {
    if (!teamMemberQuery.data) return []

    const members = teamMemberQuery.data.data.rows

    return members.sort((a, b) => {
      const isCurrentUserA = user?.id === a.user?.id
      const isCurrentUserB = user?.id === b.user?.id

      // Current user always first
      if (isCurrentUserA) return -1
      if (isCurrentUserB) return 1

      const isRegisteredA = !!a.user
      const isRegisteredB = !!b.user

      // Registered users before unregistered
      if (isRegisteredA && !isRegisteredB) return -1
      if (!isRegisteredA && isRegisteredB) return 1

      // Within each group, sort by createdAt (newest to oldest)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  }, [teamMemberQuery, user])

  const handleRefetchTeamMembers = useCallback(async () => {
    await teamMemberQuery.refetch()
  }, [teamMemberQuery])

  return (
    <TeamsContext.Provider
      value={{
        activeTeamId,
        activeTeamName,
        activeTeamType,
        activeTeamCarePerson,
        concernProfileCarePersonId,
        onChangeTeam: handleChangeTeam,
        onChangeTeamToDefault: handleChangeTeamToDefault,
        checkIsDefault,
        checkIsSystemManaged,
        teamsData,
        teamMembersData,
        isTeamMembersLoading: teamMemberQuery.isFetching,
        onRefetchTeams: handleRefetchTeams,
        onRefetchTeamMembers: handleRefetchTeamMembers,
      }}
    >
      {children}
    </TeamsContext.Provider>
  )
}

export function useTeams() {
  const context = useContext(TeamsContext)
  if (!context) {
    throw new Error('useTeams must be used within a TeamsProvider')
  }
  return context
}

/** Returns null outside TeamsProvider (e.g. admin layouts that still mount shared drawers). */
export function useOptionalTeams() {
  return useContext(TeamsContext)
}
